// Self-hosted Inter webfont for Malachite.
//
// Malachite renders inside a shadow root and injects its Tailwind sheet there, but
// @font-face rules don't load fonts from inside a shadow root — they have to live in
// the document. So we bundle the Inter woff2 files (via @fontsource/inter) and inject
// the @font-face declarations into document.head once, the first time a Malachite
// context mounts. The --font-sans theme token (see tailwind.css) then resolves to it.
import interLatin400 from '@fontsource/inter/files/inter-latin-400-normal.woff2';
import interLatin500 from '@fontsource/inter/files/inter-latin-500-normal.woff2';
import interLatin600 from '@fontsource/inter/files/inter-latin-600-normal.woff2';
import interLatin700 from '@fontsource/inter/files/inter-latin-700-normal.woff2';
import interLatinExt400 from '@fontsource/inter/files/inter-latin-ext-400-normal.woff2';
import interLatinExt500 from '@fontsource/inter/files/inter-latin-ext-500-normal.woff2';
import interLatinExt600 from '@fontsource/inter/files/inter-latin-ext-600-normal.woff2';
import interLatinExt700 from '@fontsource/inter/files/inter-latin-ext-700-normal.woff2';

const LATIN_RANGE =
    'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD';
const LATIN_EXT_RANGE =
    'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF';

const FACES: ReadonlyArray<{ weight: number; src: string; range: string }> = [
    { weight: 400, src: interLatin400, range: LATIN_RANGE },
    { weight: 500, src: interLatin500, range: LATIN_RANGE },
    { weight: 600, src: interLatin600, range: LATIN_RANGE },
    { weight: 700, src: interLatin700, range: LATIN_RANGE },
    { weight: 400, src: interLatinExt400, range: LATIN_EXT_RANGE },
    { weight: 500, src: interLatinExt500, range: LATIN_EXT_RANGE },
    { weight: 600, src: interLatinExt600, range: LATIN_EXT_RANGE },
    { weight: 700, src: interLatinExt700, range: LATIN_EXT_RANGE },
];

const STYLE_ID = 'malachite-inter-font-face';

let injected = false;

export const ensureInterFontFace = (): void => {
    if (injected || typeof document === 'undefined' || !document.head) {
        return;
    }
    if (document.getElementById(STYLE_ID)) {
        injected = true;
        return;
    }
    const css = FACES.map(
        ({ weight, src, range }) =>
            `@font-face{font-family:'Inter';font-style:normal;font-weight:${weight};font-display:swap;src:url('${src}') format('woff2');unicode-range:${range};}`,
    ).join('');
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
    injected = true;
};
